import { useEffect, useState, useCallback } from 'react';
import { z } from 'zod';

// -- Message types --

/**
 * Init message sent by the parent to Ronan via postMessage. `intentId` and
 * `nonce` are optional; the rest are required for the handshake. `webhookUri`
 * is the server-to-server callback the parent asks the auth code to be
 * delivered to.
 */
const initMessageSchema = z.object({
  type: z.literal('ronan:init'),
  clientId: z.string().min(1),
  scope: z.string().min(1),
  state: z.string().min(1),
  codeChallenge: z.string().min(1),
  webhookUri: z.string().min(1),
  intentId: z.string().optional(),
  nonce: z.string().optional(),
});

export type RonanInitMessage = z.infer<typeof initMessageSchema>;

/**
 * Messages sent by Ronan to the parent via postMessage.
 */
export type RonanParentMessage =
  | { type: 'ronan:ready' }
  | { type: 'ronan:complete' }
  | { type: 'ronan:error'; error: string }
  | { type: 'ronan:cancel' };

// -- Hook --

/**
 * useParentMessaging — postMessage bridge with an init-time handshake.
 *
 * Protocol:
 * 1. Parent sends `ronan:init` with the flow parameters.
 * 2. Ronan records `event.origin`.
 * 3. After reading the interaction context, Ronan verifies the saved origin
 *    against the server-supplied `allowedEmbedOrigins`.
 * 4. If allowed, all subsequent messages target only that origin.
 */
export function useParentMessaging(isEmbedded: boolean) {
  const [verifiedOrigin, setVerifiedOrigin] = useState<string | null>(null);
  const [initData, setInitData] = useState<RonanInitMessage | null>(null);
  const [handshakeError, setHandshakeError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEmbedded) return;

    const handler = (event: MessageEvent) => {
      const data = event.data;
      if (!data || data.type !== 'ronan:init') return;

      const parsed = initMessageSchema.safeParse(data);
      if (!parsed.success) {
        const missing = parsed.error.issues
          .map((issue) => issue.path.join('.'))
          .join(', ');
        const error = `[Ronan] ronan:init is missing or has invalid required fields: ${missing}`;
        console.error(error, data);
        setHandshakeError(error);
        return;
      }

      setInitData(parsed.data);
      // Save the origin before verification — checked later via verifyOrigin.
      setVerifiedOrigin(event.origin);
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [isEmbedded]);

  /**
   * Verify the saved parent origin against the server-supplied
   * `allowedEmbedOrigins`. Returns true if allowed; otherwise records a
   * handshake error, clears the saved origin, and returns false.
   */
  const verifyOrigin = useCallback(
    (allowedEmbedOrigins: string[]): boolean => {
      if (!verifiedOrigin) return false;
      if (!allowedEmbedOrigins.includes(verifiedOrigin)) {
        setHandshakeError(
          `Parent origin "${verifiedOrigin}" is not in the allowed origins list.`,
        );
        setVerifiedOrigin(null);
        return false;
      }
      return true;
    },
    [verifiedOrigin],
  );

  /**
   * Send a message to the verified parent origin. Silently drops if not
   * embedded or the origin has not been verified/pinned yet.
   */
  const notify = useCallback(
    (message: RonanParentMessage) => {
      if (!isEmbedded || !verifiedOrigin) return;
      window.parent.postMessage(message, verifiedOrigin);
    },
    [isEmbedded, verifiedOrigin],
  );

  return { notify, verifiedOrigin, initData, handshakeError, verifyOrigin };
}
